(() => {
  const COMPONENT = 'trio-pack-selector';
  const DESIGN_KEY = 'Design selection';
  const PACK_KEY = 'Purchase option';

  class TrioPackSelector extends HTMLElement {
    connectedCallback() {
      this.priceMatrix = this.#readPriceMatrix();
      this.bundlePriceMatrix = this.#readBundlePriceMatrix();
      this.section = this.closest('.shopify-section');
      this.form = this.section?.querySelector('product-form-component form[data-type="add-to-cart-form"]');
      if (!this.form) return;
      this.bundleVariants = this.#readBundleVariants();

      this.choiceButtons = Array.from(this.querySelectorAll('[data-trio-mode]'));
      this.selectionStatus = this.querySelector('[data-trio-selection-status]');
      this.selectedMode = 'single';
      this.selectedDesign = this.choiceButtons[0]?.dataset.trioDesign || '';
      this.nativeVariantId = this.#formVariantId();
      // The native Horizon picker refreshes the price DOM after a Type / Size /
      // Color change. Re-apply only this SKU's bundle display price if that
      // refresh happens while 3-PACK is selected; single-item prices stay native.
      this.priceObserver = new MutationObserver(() => {
        if (this.selectedMode === 'bundle') {
          const bundlePrice = this.bundlePriceMatrix[this.#selectedType()];
          if (bundlePrice) this.#setMainPrice(bundlePrice.price, bundlePrice.compareAtPrice, true);
        }
      });
      this.priceObserver.observe(this.section, { childList: true, subtree: true, characterData: true });

      this.choiceButtons.forEach((button) => button.addEventListener('click', () => this.#select(button)));
      // Horizon may refresh the hidden variant field while its own picker settles.
      // These two form-level hooks run against the final request data, so a bundle
      // selection cannot be converted back to the single-shirt variant at submit time.
      this.form.addEventListener('submit', () => this.#syncFormForSubmission(), true);
      this.form.addEventListener('formdata', (event) => this.#syncFormData(event.formData));
      // Shop Pay can start checkout directly from its web component instead of
      // the normal Add to cart path. Capture its pointer and click events before
      // its own handler reads the form, then apply the same bundle payload.
      this.form.addEventListener('pointerdown', (event) => this.#prepareAcceleratedCheckout(event), true);
      this.form.addEventListener('click', (event) => this.#prepareAcceleratedCheckout(event), true);
      this.section.addEventListener('change', (event) => {
        if (this.contains(event.target)) return;
        window.requestAnimationFrame(() => {
          this.nativeVariantId = this.#formVariantId();
          this.#sync();
        });
      }, { passive: true });
      this.#hideNativeQuantitySelector();
      this.#sync();
    }

    #readPriceMatrix() {
      try {
        return JSON.parse(this.dataset.priceMatrix || '{}');
      } catch {
        return {};
      }
    }

    #readBundlePriceMatrix() {
      try {
        return JSON.parse(this.dataset.bundlePriceMatrix || '{}');
      } catch {
        return {};
      }
    }

    #readBundleVariants() {
      const mapNode = this.section?.querySelector('[data-trio-bundle-variant-map]');
      try {
        return JSON.parse(mapNode?.textContent || '{}');
      } catch {
        return {};
      }
    }

    #select(button) {
      this.selectedMode = button.dataset.trioMode || 'single';
      this.selectedDesign = button.dataset.trioDesign || '';
      this.choiceButtons.forEach((choice) => {
        const selected = choice === button;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-checked', String(selected));
      });
      this.#sync();
    }

    #selectedType() {
      const knownTypes = Object.keys(this.priceMatrix);
      const checked = Array.from(this.section?.querySelectorAll('input[type="radio"]') || []).find(
        (input) => input.checked && knownTypes.includes(input.value)
      );
      return checked?.value || 'T-shirt';
    }

    #selectedSize() {
      const sizes = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
      return Array.from(this.section?.querySelectorAll('input[type="radio"]:checked') || []).find((input) => sizes.includes(input.value))?.value || 'S';
    }

    #bundleVariantId() {
      const type = this.#selectedType();
      const size = this.#selectedSize();
      const checkedValues = Array.from(this.section?.querySelectorAll('input[type="radio"]:checked') || []).map((input) => input.value);
      return checkedValues
        .map((color) => this.bundleVariants[`${type}::${size}::${color}`])
        .find(Boolean) || null;
    }

    #money(cents) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    }

    #ensureProperty(name) {
      let input = this.form.querySelector(`input[data-dg-trio-property="${CSS.escape(name)}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.dgTrioProperty = name;
        this.form.append(input);
      }
      return input;
    }

    #setQuantity(quantity) {
      const quantityInput = this.form.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = String(quantity);
      }
    }

    #formVariantInput() {
      return this.form.querySelector('input[name="id"]');
    }

    #formVariantId() {
      return this.#formVariantInput()?.value || null;
    }

    #setFormVariantId(variantId) {
      const input = this.#formVariantInput();
      if (!input || !variantId) return false;
      input.value = variantId;
      input.setAttribute('value', variantId);
      return true;
    }

    #bundleContents() {
      return 'Liberal Acrostic + 0% Liberal + My Dog Is A Democrat';
    }

    #syncPurchaseMetadata() {
      const isBundle = this.selectedMode === 'bundle';
      this.#ensureProperty(DESIGN_KEY).value = isBundle ? this.#bundleContents() : this.selectedDesign;
      this.#ensureProperty(PACK_KEY).value = isBundle ? '3-shirt bundle' : 'Single shirt';
      this.#setQuantity(1);
    }

    #syncAcceleratedCheckoutVariant(bundleVariantId = null) {
      const accelerated = this.form.querySelector('shopify-accelerated-checkout');
      if (!(accelerated instanceof HTMLElement)) return;
      try {
        const existing = JSON.parse(accelerated.getAttribute('variant-params') || '[]');
        if (!Array.isArray(existing)) return;
        // The native component lists only its normal product variants. Keep
        // that list intact and swap in at most the one companion variant that
        // matches the shopper's current 3-PACK choices.
        const companionIds = new Set(Object.values(this.bundleVariants).map(String));
        const next = existing.filter((variant) => !companionIds.has(String(variant?.id || '')));
        if (bundleVariantId) next.push({ id: Number(bundleVariantId), requiresShipping: true });
        const serialized = JSON.stringify(next);
        if (accelerated.getAttribute('variant-params') !== serialized) {
          accelerated.setAttribute('variant-params', serialized);
        }
      } catch {
        // Leave Shopify's native button untouched if its generated metadata is unavailable.
      }
    }

    #prepareAcceleratedCheckout(event) {
      const accelerated = this.form.querySelector('shopify-accelerated-checkout');
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      if (accelerated && (path.includes(accelerated) || accelerated.contains(event.target))) {
        this.#syncFormForSubmission();
        this.#syncAcceleratedCheckoutVariant(this.selectedMode === 'bundle' ? this.#bundleVariantId() : null);
      }
    }

    #syncFormForSubmission() {
      this.#syncPurchaseMetadata();
      if (this.selectedMode === 'bundle') {
        const bundleVariantId = this.#bundleVariantId();
        if (bundleVariantId) this.#setFormVariantId(bundleVariantId);
        return;
      }
      // Preserve the currently selected native Type / Size / Clothing Color
      // for every individual design, including an immediate switch from 3-PACK.
      const nativeVariantId = this.#formVariantId() || this.nativeVariantId;
      if (nativeVariantId) {
        this.nativeVariantId = nativeVariantId;
        this.#setFormVariantId(nativeVariantId);
      }
    }

    #syncFormData(formData) {
      if (!(formData instanceof FormData)) return;
      if (this.selectedMode === 'bundle') {
        const bundleVariantId = this.#bundleVariantId();
        if (!bundleVariantId) return;
        formData.set('id', bundleVariantId);
        formData.set('quantity', '1');
        formData.set(`properties[${DESIGN_KEY}]`, this.#bundleContents());
        formData.set(`properties[${PACK_KEY}]`, '3-shirt bundle');
        return;
      }
      const nativeVariantId = this.#formVariantId() || this.nativeVariantId;
      if (nativeVariantId) formData.set('id', nativeVariantId);
      formData.set('quantity', '1');
      formData.set(`properties[${DESIGN_KEY}]`, this.selectedDesign);
      formData.set(`properties[${PACK_KEY}]`, 'Single shirt');
    }

    #setAddToCartEnabled(enabled) {
      const button = this.form.querySelector('button[type="submit"]');
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = !enabled;
      button.setAttribute('aria-disabled', String(!enabled));
    }

    #hideNativeQuantitySelector() {
      const selector = this.form.querySelector('quantity-selector-component');
      if (selector instanceof HTMLElement) selector.style.display = 'none';
    }

    #setAcceleratedCheckoutVisible(visible) {
      const accelerated = this.form.querySelector('shopify-accelerated-checkout');
      if (accelerated instanceof HTMLElement) {
        accelerated.style.display = visible ? '' : 'none';
        accelerated.setAttribute('aria-hidden', String(!visible));
      }
    }

    #setMainPrice(saleCents, compareAtCents, isBundle) {
      const saleNodes = Array.from(this.section.querySelectorAll('.price-item--sale.price, .price__regular .price'));
      const compareNodes = Array.from(this.section.querySelectorAll('.price-item--regular.compare-at-price'));
      if (!isBundle) {
        [...saleNodes, ...compareNodes].forEach((node) => {
          const nativePrice = node.dataset.dgTrioNativePrice;
          if (nativePrice && node.textContent.trim() !== nativePrice) node.textContent = nativePrice;
          node.removeAttribute('data-dg-trio-display-price');
          node.removeAttribute('data-dg-trio-native-price');
        });
        return;
      }
      const sale = this.#money(saleCents);
      const compareAt = this.#money(compareAtCents);
      const applyPrice = (node, value) => {
        const current = node.textContent.trim();
        // Capture a newly rendered native price before replacing it. This lets
        // the exact Type / Size / Color price return when the shopper switches
        // back from 3-PACK to a single shirt.
        if (node.dataset.dgTrioDisplayPrice !== current) node.dataset.dgTrioNativePrice = current;
        if (current !== value) node.textContent = value;
        node.dataset.dgTrioDisplayPrice = value;
      };

      saleNodes.forEach((node) => applyPrice(node, sale));
      compareNodes.forEach((node) => applyPrice(node, compareAt));
    }

    #sync() {
      const isBundle = this.selectedMode === 'bundle';
      const bundlePrice = this.bundlePriceMatrix[this.#selectedType()];

      this.#syncPurchaseMetadata();
      // Keep the native Shop Pay button visible for the three-pack as well.
      // Its parent container continues to own native sold-out visibility.
      this.#setAcceleratedCheckoutVisible(true);
      const bundleVariantId = isBundle ? this.#bundleVariantId() : null;
      this.#syncAcceleratedCheckoutVariant(bundleVariantId);
      if (!isBundle) {
        this.#setFormVariantId(this.nativeVariantId);
        this.#setAddToCartEnabled(Boolean(this.nativeVariantId));
        this.#setMainPrice(0, 0, false);
      } else {
        const isReady = Boolean(bundleVariantId && bundlePrice);
        this.#setFormVariantId(bundleVariantId);
        this.#setAddToCartEnabled(isReady);
        if (isReady) this.#setMainPrice(bundlePrice.price, bundlePrice.compareAtPrice, true);
      }
      window.requestAnimationFrame(() => {
        if (isBundle) {
          const bundleVariantId = this.#bundleVariantId();
          if (bundleVariantId) this.#setFormVariantId(bundleVariantId);
        } else {
          this.#setFormVariantId(this.nativeVariantId);
        }
      });

      if (this.selectionStatus) this.selectionStatus.textContent = isBundle ? '3 shirts · all designs' : '1 shirt';
    }
  }

  if (!customElements.get(COMPONENT)) customElements.define(COMPONENT, TrioPackSelector);
})();
